import { useMutation } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client } from "../client";

export const useCreateWorkspace = () => {
  return useMutation({
    mutationFn: async (payload: { name: string; customerType: "brand" | "agency" }) => {
      const res = await client.admin.workspace.$post({ json: payload });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
};

export const useUpdateWorkspace = () => {
  return useMutation({
    mutationFn: async ({
      id,
      name,
      customerType,
    }: {
      id: string;
      name?: string;
      customerType?: "brand" | "agency";
    }) => {
      const res = await client.admin.workspace[":id"].$put({ param: { id }, json: { name, customerType } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "workspace", variables.id] });
    },
  });
};

export const useDeleteWorkspace = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.admin.workspace[":id"].$delete({ param: { id } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
};

export const useAddMerchantsToWorkspace = () => {
  return useMutation({
    mutationFn: async ({ workspaceId, merchantIds }: { workspaceId: string; merchantIds: string[] }) => {
      const res = await client.admin.workspace[":id"].merchants.$post({
        param: { id: workspaceId },
        json: { merchantIds },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspace", variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
};

export const useRemoveMerchantFromWorkspace = () => {
  return useMutation({
    mutationFn: async ({ workspaceId, merchantId }: { workspaceId: string; merchantId: string }) => {
      const res = await client.admin.workspace[":id"].merchants[":merchantId"].$delete({
        param: { id: workspaceId, merchantId },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspace", variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
    },
  });
};

export const useAddUsersToWorkspace = () => {
  return useMutation({
    mutationFn: async ({
      workspaceId,
      userIds,
      merchantIds,
      merchants,
      workspaceAdmin,
    }: {
      workspaceId: string;
      userIds: string[];
      // Legacy: all brands granted at role "user".
      merchantIds?: string[];
      // Per-brand scoped roles (UNB-2001) — keys are the granted brands.
      merchants?: Record<string, "user" | "merchant_admin">;
      workspaceAdmin?: boolean;
    }) => {
      const res = await client.admin.workspace[":id"].users.$post({
        param: { id: workspaceId },
        json: { userIds, merchantIds, merchants, workspaceAdmin },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspace", variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
};

/** Replace a user's whole workspace access in one request. */
export const useSetUserWorkspaceMemberships = () => {
  return useMutation({
    mutationFn: async ({
      userId,
      memberships,
    }: {
      userId: string;
      memberships: Record<string, { workspaceAdmin: boolean; merchants: Record<string, "user" | "merchant_admin"> }>;
    }) => {
      const res = await client.admin.user[":id"]["workspace-memberships"].$put({
        param: { id: userId },
        json: { memberships },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspace"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
};

export const useRemoveUserFromWorkspace = () => {
  return useMutation({
    mutationFn: async ({ workspaceId, userId }: { workspaceId: string; userId: string }) => {
      const res = await client.admin.workspace[":id"].users[":userId"].$delete({
        param: { id: workspaceId, userId },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspace", variables.workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
};

export const useUploadWorkspaceLogo = () => {
  return useMutation({
    mutationFn: async ({ workspaceId, file }: { workspaceId: string; file: File }) => {
      const res = await client.admin.workspace[":id"].logo.$post({
        param: { id: workspaceId },
        form: { logo: file },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "workspace", variables.workspaceId] });
    },
  });
};

export const useDeleteWorkspaceLogo = () => {
  return useMutation({
    mutationFn: async (workspaceId: string) => {
      const res = await client.admin.workspace[":id"].logo.$delete({
        param: { id: workspaceId },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_data, workspaceId) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "workspace", workspaceId] });
    },
  });
};
